/**
 * Deep-link helpers for instrument routes (launch screen → builder, list hub redirects).
 *
 * Workspace routes (`/instruments/:id/builder|routing`) and create flow routes carry
 * resource ids in the path only. Campaign + LOI for create flow use sessionStorage.
 */

import { rememberInstrumentCreateContext } from './instrumentCreateSession'

/** Campaign overview / launch home (`CampaignDetailPage`). */
export function campaignHomePath(campaignId: string): string {
  return `/campaigns/${encodeURIComponent(campaignId)}`
}

/** BFP-999 Get Started decision screen. */
export function researchGetStartedPath(): string {
  return '/campaigns/get-started'
}

/**
 * AppShell back + leave-confirm target from the question builder (not the info step).
 */
export function questionBuilderBackPath(campaignId: string | null | undefined): string {
  const id = campaignId?.trim()
  // BFP-999 — quick instruments return to Direct Instrument Building tab.
  return id ? campaignHomePath(id) : '/campaigns?tab=direct'
}

/** Pre-create picker — stores campaign + LOI in session, then navigates to a clean URL. */
export function instrumentCreatePath(campaignId: string, loiId: string): string {
  rememberInstrumentCreateContext({ origin: 'campaign', campaignId, loiId })
  return '/instruments/create'
}

/** BFP-999 — Direct / Quick create (no Campaign/LOI). */
export function instrumentQuickCreatePath(): string {
  rememberInstrumentCreateContext({ origin: 'quick', campaignId: '', loiId: '' })
  return '/instruments/create'
}

/** Instrument dashboard (quick publish success + Direct tab View results). */
export function instrumentDashboardPath(instrumentId: string): string {
  return `/instrument-dashboard/${encodeURIComponent(instrumentId)}`
}

/**
 * Dashboard / list open target for Direct Instruments.
 * Drafts have no response dashboard — open the question builder instead.
 */
export function directInstrumentOpenPath(instrumentId: string, status: string): string {
  return status.trim().toLowerCase() === 'draft'
    ? instrumentBuilderPath(instrumentId)
    : instrumentDashboardPath(instrumentId)
}

export function instrumentCreateInfoPath(options?: {
  templateId?: string
  builtInInstrumentId?: string
}): string {
  const base = '/instruments/create/info'
  const builtInInstrumentId = options?.builtInInstrumentId?.trim()
  if (builtInInstrumentId) {
    return `${base}?builtInInstrumentId=${encodeURIComponent(builtInInstrumentId)}`
  }
  const templateId = options?.templateId?.trim()
  if (!templateId) return base
  return `${base}?templateId=${encodeURIComponent(templateId)}`
}

export function instrumentCreateTemplatesPath(): string {
  return '/instruments/create/templates'
}

export type PublishQueryExtra = {
  instrumentTitle?: string
  loiSubtitle?: string
  setupStepLabel?: string
  loiId?: string
  estimatedCompletionTime?: string
  rewardAmount?: string
  /** When true, Continue on an edit screen returns to Step 5 Preview. */
  returnToPreview?: boolean
}

export function instrumentBuilderPath(
  instrumentId: string,
  extra?: PublishQueryExtra,
): string {
  const base = `/instruments/${encodeURIComponent(instrumentId)}/builder`
  const q = returnToPreviewSearch(extra)
  return q ? `${base}?${q}` : base
}

/** Optional query — only `returnToPreview` when returning from Step 5 Preview. */
function returnToPreviewSearch(extra?: PublishQueryExtra): string {
  if (!extra?.returnToPreview) return ''
  return 'returnToPreview=1'
}

export function instrumentRoutingPath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  const base = `/instruments/${encodeURIComponent(instrumentId)}/routing`
  const q = returnToPreviewSearch(extra)
  return q ? `${base}?${q}` : base
}

/** AppShell back from routing → question builder. */
export function instrumentRoutingBackPath(instrumentId: string): string {
  return instrumentBuilderPath(instrumentId)
}

/** Draft metadata edit from launch screen — clean path with instrument id. */
export function instrumentInfoContinuePath(
  _campaignId: string,
  _loiId: string,
  instrumentId: string,
  extra?: PublishQueryExtra,
): string {
  return instrumentInfoEditPath(instrumentId, extra)
}

export function instrumentInfoEditPath(
  instrumentId: string,
  extra?: PublishQueryExtra,
): string {
  const base = `/instruments/${encodeURIComponent(instrumentId)}/info`
  const q = returnToPreviewSearch(extra)
  return q ? `${base}?${q}` : base
}

function publishWizardPath(
  instrumentId: string,
  step:
    | 'target-audience'
    | 'distribution-channels'
    | 'instrument-type'
    | 'campaign-budget'
    | 'review-publish',
  extra?: PublishQueryExtra,
): string {
  const base = `/instruments/${encodeURIComponent(instrumentId)}/publish/${step}`
  const q = returnToPreviewSearch(extra)
  return q ? `${base}?${q}` : base
}

/** @param _campaignId Deprecated — resolved from instrument API; kept for call-site compatibility. */
export function targetAudiencePath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return publishWizardPath(instrumentId, 'target-audience', extra)
}

export function distributionChannelsPath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return publishWizardPath(instrumentId, 'distribution-channels', extra)
}

export function instrumentTypePath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return publishWizardPath(instrumentId, 'instrument-type', extra)
}

export function campaignBudgetPath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return publishWizardPath(instrumentId, 'campaign-budget', extra)
}

export function reviewPublishPath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return publishWizardPath(instrumentId, 'review-publish', extra)
}

export function publishBackToRoutingPath(
  instrumentId: string,
  _campaignId?: string,
  extra?: PublishQueryExtra,
): string {
  return instrumentRoutingPath(instrumentId, undefined, extra)
}

const PUBLISH_WIZARD_PATH_RE =
  /^\/instruments\/[^/]+\/publish\/(target-audience|distribution-channels|instrument-type|campaign-budget|review-publish)\/?$/

export type PublishWizardPathSlug =
  | 'target-audience'
  | 'distribution-channels'
  | 'instrument-type'
  | 'campaign-budget'
  | 'review-publish'

export function parsePublishWizardSlug(pathname: string): PublishWizardPathSlug | null {
  const slug = PUBLISH_WIZARD_PATH_RE.exec(pathname)?.[1]
  return slug ? (slug as PublishWizardPathSlug) : null
}

/**
 * AppShell back target inside the publish wizard.
 * Step 5 Preview → Stage 4 Campaign Budget / Reward; earlier steps walk back;
 * Step 1 Target Audience → routing (leave the publish flow).
 */
export function publishWizardBackPath(pathname: string, instrumentId: string): string {
  switch (parsePublishWizardSlug(pathname)) {
    case 'review-publish':
      return campaignBudgetPath(instrumentId)
    case 'campaign-budget':
      return instrumentTypePath(instrumentId)
    case 'instrument-type':
      return distributionChannelsPath(instrumentId)
    case 'distribution-channels':
      return targetAudiencePath(instrumentId)
    default:
      return publishBackToRoutingPath(instrumentId)
  }
}

export function publishWizardBackAriaLabel(pathname: string): string {
  switch (parsePublishWizardSlug(pathname)) {
    case 'review-publish':
      return 'Back to campaign budget'
    case 'campaign-budget':
      return 'Back to instrument type'
    case 'instrument-type':
      return 'Back to distribution channels'
    case 'distribution-channels':
      return 'Back to target audience'
    default:
      return 'Back to routing and logic'
  }
}

export function resolveInstrumentListRedirect(
  searchParams: URLSearchParams,
): string | null {
  const campaignId = searchParams.get('campaignId') ?? ''
  const loiId = searchParams.get('loiId') ?? ''
  const instrumentId = searchParams.get('instrumentId') ?? ''
  const isContinue = searchParams.get('continue') === '1'

  if (isContinue && campaignId && loiId && instrumentId) {
    rememberInstrumentCreateContext({ origin: 'campaign', campaignId, loiId })
    return instrumentInfoEditPath(instrumentId)
  }

  return null
}
